import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const AdminEditProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    collectionName: "",
    fit: "",
    model: "",
    type: "",
    slug: "",
    description: "",
    price: "",
    sale: {
      isOnSale: false,
      discountType: "percentage",
      discountValue: 0,
    },
  });

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        console.log('🔍 [AdminEditProduct] Starting to fetch product...');
        console.log('📋 [AdminEditProduct] Product ID from URL:', id);
        console.log('🌐 [AdminEditProduct] API URL:', `${API_URL}/api/products/details/${id}`);

        const res = await axios.get(`${API_URL}/api/products/details/${id}`);

        console.log('✅ [AdminEditProduct] API Response received:', res);
        console.log('📦 [AdminEditProduct] Response data:', res.data);

        const product = res.data;

        console.log('🔍 [AdminEditProduct] Product object:', product);
        console.log('📝 [AdminEditProduct] Product fields:', {
          collectionName: product.collectionName,
          fit: product.fit,
          model: product.model,
          type: product.type,
          slug: product.slug,
          description: product.description,
          price: product.price,
          sale: product.sale
        });

        const formData = {
          collectionName: product.collectionName || "",
          fit: product.fit || "",
          model: product.model || "",
          type: product.type || "",
          slug: product.slug || "",
          description: product.description || "",
          price: product.price || "",
          sale: {
            isOnSale: product.sale?.isOnSale || false,
            discountType: product.sale?.discountType || "percentage",
            discountValue: product.sale?.discountValue || 0,
          },
        };

        console.log('📋 [AdminEditProduct] Form data to set:', formData);
        setForm(formData);
        console.log('✨ [AdminEditProduct] Form state updated!');
      } catch (err) {
        console.error('❌ [AdminEditProduct] Error fetching product:', err);
        console.error('❌ [AdminEditProduct] Error response:', err.response);
        console.error('❌ [AdminEditProduct] Error message:', err.message);
        toast.error("Failed to load product");
        navigate("/admin/products");
      } finally {
        console.log('🏁 [AdminEditProduct] Setting loading to false');
        setLoading(false);
      }
    };

    console.log('🚀 [AdminEditProduct] useEffect triggered, calling fetchProduct...');
    fetchProduct();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSaleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      sale: {
        ...form.sale,
        [name]: type === "checkbox" ? checked : value,
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.put(`${API_URL}/api/products/${id}`, {
        ...form,
        price: Number(form.price),
        sale: form.sale,
      });

      toast.success("Product updated successfully");
      navigate("/admin/products");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error updating product");
    }
  };

  if (loading) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading product...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Edit Product</h2>
            <p style={styles.subtitle}>Update product details</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/admin/products")}
            style={styles.backBtn}
          >
            ← Back
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Basic Info */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>📋 Basic Information</h3>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Collection</label>
              <select
                name="collectionName"
                value={form.collectionName}
                onChange={handleChange}
                required
                style={styles.input}
              >
                <option value="">Select collection</option>
                <option value="hoodies-Pants">Hoodies-Pants</option>
                <option value="shorts">Shorts</option>
                <option value="tshirts">T-Shirts</option>
                <option value="jackets">Jackets</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Model Name</label>
              <input
                placeholder="Model"
                name="model"
                value={form.model}
                onChange={handleChange}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Slug (unique URL)</label>
              <input
                placeholder="product-slug"
                name="slug"
                value={form.slug}
                onChange={handleChange}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.row}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Type</label>
                <input
                  placeholder="t-shirt, hoodie..."
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Fit</label>
                <input
                  placeholder="oversize, regular..."
                  name="fit"
                  value={form.fit}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                placeholder="Product description"
                name="description"
                value={form.description}
                onChange={handleChange}
                style={{ ...styles.input, minHeight: "100px", resize: "vertical" }}
              />
            </div>
          </div>

          {/* Pricing */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>💰 Pricing</h3>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Base Price (DT)</label>
              <input
                type="number"
                placeholder="Base Price"
                name="price"
                value={form.price}
                onChange={handleChange}
                required
                style={styles.input}
              />
            </div>

            <label style={styles.checkbox}>
              <input
                type="checkbox"
                name="isOnSale"
                checked={form.sale.isOnSale}
                onChange={handleSaleChange}
              />
              <span style={styles.checkboxLabel}>
                🏷️ This product is on sale
              </span>
            </label>

            {form.sale.isOnSale && (
              <div style={styles.saleSection}>
                <div style={styles.row}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Discount Type</label>
                    <select
                      name="discountType"
                      value={form.sale.discountType}
                      onChange={handleSaleChange}
                      style={styles.input}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="amount">Fixed Amount (DT)</option>
                    </select>
                  </div>

                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Discount Value</label>
                    <input
                      type="number"
                      name="discountValue"
                      placeholder={form.sale.discountType === "percentage" ? "e.g., 20" : "e.g., 50"}
                      value={form.sale.discountValue}
                      onChange={handleSaleChange}
                      style={styles.input}
                    />
                  </div>
                </div>

                {form.sale.discountValue > 0 && (
                  <div style={styles.pricePreview}>
                    <span style={styles.previewLabel}>Final Price:</span>
                    <span style={styles.previewPrice}>
                      {form.sale.discountType === "percentage"
                        ? (form.price - (form.price * form.sale.discountValue) / 100).toFixed(2)
                        : (form.price - form.sale.discountValue).toFixed(2)}{" "}
                      DT
                    </span>
                    <span style={styles.previewOriginal}>{form.price} DT</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={styles.buttonGroup}>
            <button
              type="button"
              onClick={() => navigate("/admin/products")}
              style={styles.cancelBtn}
            >
              Cancel
            </button>
            <button type="submit" style={styles.submitBtn}>
              💾 Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    minHeight: "100vh",
    padding: "32px",
    background: "#f8f9fa",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    gap: "16px",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid #e0e0e0",
    borderTop: "4px solid #111",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    fontSize: "16px",
    color: "#666",
    fontWeight: "500",
  },
  card: {
    maxWidth: "800px",
    margin: "0 auto",
    padding: "32px",
    borderRadius: "12px",
    background: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "32px",
    paddingBottom: "24px",
    borderBottom: "2px solid #f0f0f0",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    marginBottom: "4px",
    color: "#111",
  },
  subtitle: {
    fontSize: "15px",
    color: "#666",
    margin: 0,
  },
  backBtn: {
    padding: "10px 20px",
    background: "#f0f0f0",
    color: "#333",
    border: "none",
    borderRadius: "8px",
    fontWeight: "600",
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "28px",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#111",
    marginBottom: "8px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#555",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  input: {
    padding: "12px 16px",
    borderRadius: "8px",
    border: "2px solid #e0e0e0",
    outline: "none",
    fontSize: "15px",
    transition: "all 0.3s ease",
    fontFamily: "inherit",
  },
  checkbox: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px",
    background: "#f8f9fa",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  checkboxLabel: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#333",
  },
  saleSection: {
    padding: "20px",
    background: "#fff8e1",
    borderRadius: "8px",
    border: "2px solid #ffe082",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  pricePreview: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px",
    background: "#fff",
    borderRadius: "8px",
    border: "2px dashed #ffb74d",
  },
  previewLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#666",
  },
  previewPrice: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#ff6b6b",
  },
  previewOriginal: {
    fontSize: "16px",
    color: "#999",
    textDecoration: "line-through",
  },
  buttonGroup: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    paddingTop: "24px",
    borderTop: "2px solid #f0f0f0",
  },
  cancelBtn: {
    padding: "12px 32px",
    background: "#f0f0f0",
    color: "#333",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  submitBtn: {
    padding: "12px 32px",
    background: "linear-gradient(135deg, #111 0%, #333 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    transition: "all 0.3s ease",
  },
};

// Add CSS for animations and hover effects
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  input:focus, textarea:focus, select:focus {
    border-color: #1976d2 !important;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1) !important;
  }
  
  button[type="submit"]:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
  }
  
  button[type="button"]:hover {
    background: #e0e0e0 !important;
    transform: translateY(-1px);
  }
  
  label[style*="checkbox"]:hover {
    background: #f0f0f0 !important;
  }
`;
document.head.appendChild(styleSheet);

export default AdminEditProduct;
